import React from 'react';
import parse, { HTMLReactParserOptions, domToReact, Element, DOMNode } from 'html-react-parser';
import { Link as RouterLink } from 'react-router-dom';

export type SpecialLinkHandler = Record<string, (e: React.MouseEvent) => void>;

/**
 * Transforms HTML content by replacing <a> tags with React Router links or custom handlers.
 */
export function transformContentHtml(html: string, handlers: SpecialLinkHandler) {
    const options: HTMLReactParserOptions = {
        replace: (domNode) => {
            if (!(domNode instanceof Element) || domNode.name !== 'a') {
                return;
            }

            const href = domNode.attribs?.href;
            if (!href) {
                return;
            }

            // Render children recursively
            const children = domToReact(domNode.children as DOMNode[], options);

            // OneTrust cookie‑settings trigger
            if (href === '#cookies-settings-tool') {
                return (
                    <a
                        key={href}
                        href={href}
                        className="ot-sdk-show-settings"
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (window.OneTrust) {
                                window.OneTrust.LoadBanner();
                                window.OneTrust.ToggleInfoDisplay();
                            }
                        }}
                    >
                        {children}
                    </a>
                );
            }

            // Custom handler links
            const handler = handlers[href];
            if (handler) {
                return (
                    <a
                        key={href}
                        href={href}
                        onClick={(e) => {
                            e.preventDefault();
                            handler(e);
                        }}
                    >
                        {children}
                    </a>
                );
            }

            // Internal app routes: relative paths
            if (href.startsWith('/')) {
                return (
                    <RouterLink key={href} to={href}>
                        {children}
                    </RouterLink>
                );
            }

            // Absolute URLs on current domain: convert to relative
            try {
                const url = new URL(href, window.location.origin);
                if (url.origin === window.location.origin) {
                    const relativePath = url.pathname + url.search + url.hash;
                    return (
                        <RouterLink key={href} to={relativePath}>
                            {children}
                        </RouterLink>
                    );
                }
            } catch {
                // If URL parsing fails, fall back to normal link
            }

            // Fallback: external or fragment links
            return (
                <a key={href} href={href}>
                    {children}
                </a>
            );
        },
    };

    return parse(html, options);
}
